// PricingPage.tsx - Página de Planos e Preços
// Conversão de visitantes em clientes pagantes

import React, { useState } from 'react';
import { Check, X, Zap, Star, Crown, ArrowRight, CreditCard, Shield } from 'lucide-react';
import { PESCAR_PLANS, calculateYearlySavings, usePayments, Plan } from '../../core/services/payments/stripeService';

export const PricingPage: React.FC = () => {
  const [isYearly, setIsYearly] = useState(true);
  const [isLoading, setIsLoading] = useState<string | null>(null);
  const { checkout } = usePayments();

  const handleSelectPlan = async (plan: Plan) => {
    setIsLoading(plan.id);
    try {
      const { url } = await checkout(plan.id, isYearly);
      window.location.href = url;
    } catch (error) {
      console.error('Checkout error:', error);
      alert('Erro ao processar. Tente novamente.');
    } finally {
      setIsLoading(null);
    }
  };

  const getIcon = (planId: string) => {
    switch (planId) {
      case 'starter': return <Zap className="w-8 h-8" />;
      case 'professional': return <Star className="w-8 h-8" />;
      case 'enterprise': return <Crown className="w-8 h-8" />;
      default: return <Zap className="w-8 h-8" />;
    }
  };

  const getGradient = (planId: string) => {
    switch (planId) {
      case 'starter': return 'from-blue-500 to-blue-600';
      case 'professional': return 'from-purple-500 to-purple-600';
      case 'enterprise': return 'from-amber-500 to-amber-600';
      default: return 'from-gray-500 to-gray-600';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 py-20 px-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-16">
          <span className="inline-block px-4 py-2 bg-blue-500/10 text-blue-400 rounded-full text-sm font-semibold mb-4">
            💰 Preços Transparentes
          </span>
          <h1 className="text-5xl font-bold text-white mb-6">
            Escolha o Plano Ideal para Seu Negócio
          </h1>
          <p className="text-xl text-slate-400 max-w-2xl mx-auto">
            Comece gratuitamente por 14 dias. Sem cartão de crédito necessário.
            Cancele a qualquer momento.
          </p>

          {/* Toggle Mensal/Anual */}
          <div className="flex items-center justify-center mt-10 gap-4">
            <span className={`text-lg ${!isYearly ? 'text-white' : 'text-slate-500'}`}>
              Mensal
            </span>
            <button
              onClick={() => setIsYearly(!isYearly)}
              className={`relative w-16 h-8 rounded-full transition-colors ${
                isYearly ? 'bg-green-500' : 'bg-slate-600'
              }`}
            >
              <div
                className={`absolute top-1 w-6 h-6 bg-white rounded-full shadow-lg transition-transform ${
                  isYearly ? 'translate-x-9' : 'translate-x-1'
                }`}
              />
            </button>
            <span className={`text-lg ${isYearly ? 'text-white' : 'text-slate-500'}`}>
              Anual
            </span>
            {isYearly && (
              <span className="ml-2 px-3 py-1 bg-green-500/20 text-green-400 rounded-full text-sm font-semibold">
                Economize 17%
              </span>
            )}
          </div>
        </div>

        {/* Plans Grid */}
        <div className="grid lg:grid-cols-3 gap-8 mb-16">
          {PESCAR_PLANS.map((plan) => {
            const { savings, percentage } = calculateYearlySavings(plan);
            const price = isYearly ? plan.priceYearly / 12 : plan.priceMonthly;
            
            return (
              <div
                key={plan.id}
                className={`relative rounded-3xl overflow-hidden transition-all duration-300 hover:scale-105 ${
                  plan.highlighted 
                    ? 'bg-gradient-to-br from-slate-800 to-slate-900 ring-2 ring-purple-500 shadow-2xl shadow-purple-500/20' 
                    : 'bg-slate-800/50 backdrop-blur-sm'
                }`}
              >
                {/* Popular Badge */}
                {plan.highlighted && (
                  <div className="absolute top-0 right-0 bg-gradient-to-r from-purple-500 to-pink-500 text-white px-4 py-1 text-sm font-semibold rounded-bl-xl">
                    🔥 Mais Popular
                  </div>
                )}

                <div className="p-8">
                  {/* Icon & Name */}
                  <div className={`inline-flex items-center justify-center w-14 h-14 rounded-xl bg-gradient-to-br ${getGradient(plan.id)} text-white mb-6`}>
                    {getIcon(plan.id)}
                  </div>
                  
                  <h3 className="text-2xl font-bold text-white mb-2">{plan.name}</h3>
                  <p className="text-slate-400 mb-6">{plan.description}</p>

                  {/* Price */}
                  <div className="mb-8">
                    <div className="flex items-baseline gap-2">
                      <span className="text-5xl font-bold text-white">
                        R${Math.round(price)}
                      </span>
                      <span className="text-slate-400">/mês</span>
                    </div>
                    {isYearly && (
                      <p className="text-green-400 text-sm mt-2">
                        ✓ Economia de R${savings}/ano ({percentage}%)
                      </p>
                    )}
                  </div>

                  {/* CTA Button */}
                  <button
                    onClick={() => handleSelectPlan(plan)}
                    disabled={isLoading === plan.id}
                    className={`w-full py-4 rounded-xl font-semibold text-lg transition-all flex items-center justify-center gap-2 ${
                      plan.highlighted
                        ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white hover:shadow-lg hover:shadow-purple-500/30'
                        : 'bg-slate-700 text-white hover:bg-slate-600'
                    }`}
                  >
                    {isLoading === plan.id ? (
                      <span className="animate-spin">⏳</span>
                    ) : (
                      <>
                        Começar Agora
                        <ArrowRight className="w-5 h-5" />
                      </>
                    )}
                  </button>

                  <p className="text-center text-slate-500 text-sm mt-4">
                    14 dias grátis • Sem compromisso
                  </p>
                </div>

                {/* Features */}
                <div className="border-t border-slate-700/50 p-8">
                  <p className="text-sm font-semibold text-slate-400 uppercase tracking-wider mb-4">
                    O que está incluso:
                  </p>
                  <ul className="space-y-3">
                    {plan.features.map((feature, i) => (
                      <li key={i} className="flex items-start gap-3 text-slate-300">
                        <Check className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            );
          })}
        </div>

        {/* Trust Badges */}
        <div className="flex flex-wrap justify-center items-center gap-8 py-8 border-t border-slate-700/50">
          <div className="flex items-center gap-2 text-slate-400">
            <Shield className="w-5 h-5 text-green-400" />
            <span>SSL Seguro</span>
          </div>
          <div className="flex items-center gap-2 text-slate-400">
            <CreditCard className="w-5 h-5 text-blue-400" />
            <span>Pagamento via Stripe</span>
          </div>
          <div className="flex items-center gap-2 text-slate-400">
            <Check className="w-5 h-5 text-purple-400" />
            <span>Cancele quando quiser</span>
          </div>
          <div className="flex items-center gap-2 text-slate-400">
            <Star className="w-5 h-5 text-amber-400" />
            <span>Suporte brasileiro</span>
          </div>
        </div>

        {/* FAQ Section */}
        <div className="mt-20">
          <h2 className="text-3xl font-bold text-white text-center mb-12">
            Perguntas Frequentes
          </h2>
          
          <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {[
              {
                q: 'Posso testar antes de pagar?',
                a: 'Sim! Todos os planos incluem 14 dias de teste gratuito. Não precisa de cartão de crédito para começar.'
              },
              {
                q: 'Como funciona o cancelamento?',
                a: 'Você pode cancelar a qualquer momento pelo painel. O acesso continua até o fim do período pago.'
              },
              {
                q: 'Posso mudar de plano depois?',
                a: 'Sim! Você pode fazer upgrade ou downgrade a qualquer momento. Ajustamos o valor proporcional.'
              },
              {
                q: 'Quais formas de pagamento?',
                a: 'Aceitamos cartão de crédito, débito, Pix e boleto via Stripe.'
              },
            ].map((faq, i) => (
              <div key={i} className="bg-slate-800/50 rounded-xl p-6">
                <h3 className="font-semibold text-white mb-2">{faq.q}</h3>
                <p className="text-slate-400">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>

        {/* CTA Final */}
        <div className="mt-20 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">
            Pronto para crescer seu negócio?
          </h2>
          <p className="text-slate-400 mb-8">
            Junte-se a centenas de pousadas e operadores que já usam o Nossa Pesca
          </p>
          <button
            onClick={() => handleSelectPlan(PESCAR_PLANS[1])} // Professional
            className="px-8 py-4 bg-gradient-to-r from-purple-500 to-pink-500 text-white font-semibold text-lg rounded-xl hover:shadow-lg hover:shadow-purple-500/30 transition-all"
          >
            Começar Teste Grátis
          </button>
        </div>
      </div>
    </div>
  );
};

export default PricingPage;
